import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  setObfuscateSensitiveData,
  setSensitiveDataProperties,
} from "@app/store/appSlice";
import { RootState } from "@app/store/store";
import React from "react";
import useApiRequest from "../common/useApiRequest";
import { UserMetaResponse } from "@shared/types/user";
import { useParams } from "react-router-dom";
import { Property } from "@features/clinic-patients/clinic/types";

export const usePIIToggle = () => {
  const dispatch = useDispatch();
  const { GET, PATCH } = useApiRequest();
  const queryClient = useQueryClient();
  const params = useParams();
  const clinicId = params.clinicId;

  const obfuscateSensitiveData = useSelector(
    (state: RootState) => state.app.obfuscateSensitiveData
  );
  const sensitiveDataProperties = useSelector(
    (state: RootState) => state.app.sensitiveDataProperties
  );

  const { data: userMeta } = useQuery<UserMetaResponse, Error>({
    queryKey: ["userMeta"],
    queryFn: () => GET<UserMetaResponse>("/api/user/me/meta"),
  });

  const { data: properties = [] } = useQuery<Property[]>({
    queryKey: ["patient-schema-properties"],
    queryFn: () =>
      GET<Property[]>(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties`
      ),
    enabled: !!clinicId,
  });

  React.useEffect(() => {
    if (userMeta) {
      dispatch(setObfuscateSensitiveData(userMeta.obfuscateSensitiveData));
    }
  }, [userMeta, dispatch]);

  React.useEffect(() => {
    if (properties.length > 0) {
      const sensitiveProperties = properties
        .filter(
          (property) =>
            property.sensitivityLevel && property.name !== "core_avatar"
        )
        .map((property) => property.name);
      dispatch(setSensitiveDataProperties(sensitiveProperties));
    }
  }, [properties, dispatch]);

  const updatePIIState = useMutation<void, Error, boolean>({
    mutationFn: async (newState: boolean) => {
      await PATCH("/api/user/me/meta", {
        obfuscateSensitiveData: newState,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userMeta"] });
    },
  });

  const handleToggle = (newState: boolean) => {
    dispatch(setObfuscateSensitiveData(newState));
    updatePIIState.mutate(newState);
  };

  return {
    obfuscateSensitiveData,
    sensitiveDataProperties,
    handleToggle,
    isUpdating: updatePIIState.isPending,
  };
};
